import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, BellRing, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["email", "sms", "in_app"];
const CH_LABEL = { email: "Email", sms: "SMS", in_app: "In-App" };
const LOG_CLS = { sent: "bg-emerald-100 text-emerald-700", queued: "bg-amber-100 text-amber-700", failed: "bg-rose-100 text-rose-700" };

export default function NotificationAdmin() {
  const { user } = useAuth();
  const [types, setTypes] = useState(null);
  const [log, setLog] = useState({ email: [], sms: [] });

  const load = () => api.get("/admin/notification-types").then(({ data }) => setTypes(data)).catch(() => setTypes([]));
  const loadLog = () => api.get("/admin/notification-log").then(({ data }) => setLog(data)).catch(() => {});
  useEffect(() => { load(); loadLog(); }, []);

  const patch = async (key, body) => {
    try { const { data } = await api.put(`/admin/notification-types/${key}`, body); setTypes((prev) => prev.map((t) => t.key === key ? { ...t, required: data.required, default_channels: data.default_channels } : t)); toast.success("Updated"); }
    catch (e) { toast.error("Failed"); }
  };
  const setRequired = (t, v) => patch(t.key, { required: v });
  const setDefault = (t, ch, v) => patch(t.key, { default_channels: { ...t.default_channels, [ch]: v } });

  const categories = types ? [...new Set(types.map((t) => t.category))] : [];

  return (
    <DashboardLayout nav={getNav(user.role)} title="Notification Types & Defaults">
      {!types ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : (
        <div className="max-w-4xl space-y-6">
          <p className="text-sm text-slate-500 flex items-center gap-1.5"><BellRing className="w-4 h-4 text-blue-600" /> Mark events Required (locks email + in-app on for users) or set the default channels applied to newly created users.</p>

          {categories.map((cat) => (
            <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200"><h3 className="font-head font-semibold text-slate-900">{cat}</h3></div>
              <div className="divide-y divide-slate-100">
                {types.filter((t) => t.category === cat).map((t) => (
                  <div key={t.key} data-testid={`type-row-${t.key}`} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-[180px]">
                      <div className="text-sm font-medium text-slate-800">{t.label}</div>
                      <div className="text-xs text-slate-400">{t.audience.join(", ")}</div>
                    </div>
                    <div className="flex items-center gap-5">
                      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Required <Switch data-testid={`type-required-${t.key}`} checked={t.required} onCheckedChange={(v) => setRequired(t, v)} /></label>
                      <div className="flex items-center gap-4 border-l border-slate-200 pl-4">
                        {CHANNELS.map((ch) => (
                          <label key={ch} className="flex items-center gap-1.5 text-xs text-slate-500">{CH_LABEL[ch]}
                            <Switch data-testid={`type-default-${t.key}-${ch}`} checked={!!t.default_channels[ch]} disabled={t.required && (ch === "email" || ch === "in_app")} onCheckedChange={(v) => setDefault(t, ch, v)} />
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-slate-900">Delivery Log</h3><Button size="sm" variant="ghost" onClick={loadLog}><RefreshCw className="w-3.5 h-3.5" /></Button></div>
            {(!log.email || log.email.length === 0) && (!log.sms || log.sms.length === 0) ? <p className="text-sm text-slate-400">No delivery activity yet.</p> : (
              <div className="space-y-1.5" data-testid="notif-log-list">
                {(log.email || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-2">
                    <div className="min-w-0"><div className="text-slate-800 truncate">{l.to} <span className="text-slate-400">· email · {l.type || "—"}</span></div><div className="text-xs text-slate-400 truncate">{l.subject}{l.error ? ` · ${l.error}` : ""} · {new Date(l.at).toLocaleString()}</div></div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${LOG_CLS[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                  </div>
                ))}
                {(log.sms || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-50 py-2">
                    <div className="min-w-0"><div className="text-slate-800 truncate">{l.to} <span className="text-slate-400">· sms</span></div><div className="text-xs text-slate-400 truncate">{l.message} · {new Date(l.created_at).toLocaleString()}</div></div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${LOG_CLS[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
